import { Badge, BadgeProps, makeStyles, mergeClasses } from '@fluentui/react-components';
import { TItem } from '@modules/items';
import { ApprovalStatus } from '@modules/approvals';

/**
 * Maps the item’s state to a user-friendly "Status" label and Fluent UI color.
 */
const getStatusBadge = (
  item: TItem
  // This could be used for custom colors
  // classes: Record<string, string>
): {
  text: string;
  color: BadgeProps['color'];
  customClassname?: string;
} | null => {
  if (item.type === 'FOLDER') return null;
  const { hasDraft, approvalStatusOfLatestVersion } = item;

  // Priority check
  if (hasDraft) {
    return { text: 'Draft', color: 'informative' }; // Default or subtle
  }
  switch (approvalStatusOfLatestVersion) {
    case ApprovalStatus.APPROVED:
      return { text: 'Approved', color: 'brand' }; // Green
    case ApprovalStatus.DECLINED:
      return { text: 'Declined', color: 'danger' }; // Red
    case ApprovalStatus.AUTHORING:
    case ApprovalStatus.REVIEWING:
    case ApprovalStatus.APPROVING:
      return { text: 'In approval', color: 'warning' }; // Yellow
    default:
      // No draft, no recognized approval states => Completed
      return { text: 'Versioned', color: 'informative' }; // Default or subtle
  }
};

const useStyles = makeStyles({
  // blueBadge: {
  //   backgroundColor: '#0078d4',
  //   color: 'white',
  // },
  root: {
    fontWeight: 'normal',
  },
});

type TProps = {
  item: TItem;
  unavailableLabel?: string; // replaces the item's own status, e.g. "No access"
};

const StatusBadge = ({ item, unavailableLabel }: TProps) => {
  const status: ReturnType<typeof getStatusBadge> = unavailableLabel
    ? { text: unavailableLabel, color: 'subtle' }
    : getStatusBadge(item); // no useMemo needed
  const classes = useStyles();

  if (!status) return null;

  return (
    <Badge color={status.color} appearance="tint" className={mergeClasses(classes.root, status.customClassname)}>
      {status.text}
    </Badge>
  );
};

export default StatusBadge;
